<template>
  <div class="ml-4 mr-4">
    <div v-if="type === 'edit'">
      <div class="row py-3 align-items-center company-header">
        <div class="col-4">
          <h2 class="company-h1 mr-3">
            <button type="button"
                    class="btn btn-back"
                    @click="$router.push('/companies')"
            />
            {{ company.name }}
          </h2>
          <template v-if="company.status">
            <span v-if="company.status.name === 'Active'"
                  :class="'btn-status btn-active'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Inactive'"
                  :class="'btn-status btn-closed'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Setup Incomplete'"
                  :class="'btn-status btn-ncover info-company'"
            >{{ company.status.name }}
              <div class="btn btn-link p-0 tippy-popup tippy-info"
                   @mouseover="showTooltipPrefix = true"
                   @mouseout="showTooltipPrefix = false"
              >
                <i class="fa fa-info-circle" />
              </div>
              <div class="popover tippy-info-popover"
                   v-if="showTooltipPrefix"
              >
                Setup incomplete is present when the company doesn't have one or more of the following: move sizes,
                arrival windows, merchant account, rates and coverage.
              </div>
            </span>
          </template>
        </div>
        <div class="col-8 d-flex align-items-center justify-content-end">
          <companies-visibility-bar
            :company="company"
            @saved="$emit('saved')"
            v-if="$_can('companies.review.request')"
          />
          <button type="button"
                  class="btn btn-primary ld-ext-right ml-5"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
                  @click="save"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
    </div>
    <template v-if="loading">
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
    <template v-else>
      <template v-if="form_type === 0">
        <CompaniesWebQuoteSelect
          :company="company"
          @setStepEditing="setFormType(1)"
          @setStepCustom="setFormType(2)"
          @setStepIframe="setFormType(3)"
        />
      </template>
      <template v-if="form_type === 1">
        <template v-if="loading">
          <vue-content-loading :width="800" :height="450">
            <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
          </vue-content-loading>
        </template>
        <template v-else>
          <div class="row l-m-b-16">
            <div class="col-6">
              <h2 class="l-header-2">Button position</h2>
              <br />
              <span class="section-disclaimer">
                You can select a button position, where you want it to appear on your website.
              </span>
              <div class="row mb-3 mt-4">
                <div class="d-flex col-12">
                  <div class="d-flex col-6 pl-0">
                    <img src="../../assets/img/quote_left.png" width="240" alt="Quote in center" />
                  </div>
                  <div class="d-flex flex-column col-6 pl-0">
                    <div class="check-field mb-3">
                      <label for="centered" :class="[buttonPosition.includes('1') ? 'check-open' : '']">
                        <span class="checkmark" />Left side
                        <input type="checkbox"
                               v-model="buttonPosition"
                               class="btn-check"
                               id="centered"
                               :value="1"
                               @change="uniqueCheck"
                        />
                      </label>
                    </div>
                    <span class="option-explanation">
                      With this option the button will be sticky in the lower left corner of the screen.
                    </span>
                  </div>
                </div>
              </div>
              <div class="row mb-3 mt-4">
                <div class="d-flex col-12">
                  <div class="d-flex col-6 pl-0">
                    <img src="../../assets/img/quote_right.png" width="240" alt="Quote on edge of screen" />
                  </div>
                  <div class="d-flex col-6 flex-column pl-0">
                    <div class="check-field mb-3">
                      <label for="sticky" :class="[buttonPosition.includes('2') ? 'check-open' : '']">
                        <span class="checkmark" />Right side
                        <input type="checkbox"
                               v-model="buttonPosition"
                               class="btn-check"
                               id="sticky"
                               :value="2"
                               @change="uniqueCheck"
                        />
                      </label>
                    </div>
                    <span class="option-explanation">
                      With this option the button will be sticky in the lower right corner of the screen.
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-6">
              <h2 class="l-header-2">Button colors</h2>
              <br />
              <span class="section-disclaimer">
                You can choose your own color for the button and text. Enter hex for desired colors.
              </span>
              <div class="row mb-4 mt-4">
                <div class="col pr-1">
                  <div class="select-wrap">
                    <label>Primary color</label>
                    <input type="text"
                           autocomplete="no"
                           class="form-control"
                           placeholder="Enter #"
                           maxlength="7"
                           v-model="solidBackground.backgroundColor"
                           @change="styleButton"
                    />
                  </div>
                </div>
                <div class="col">
                  <div class="select-wrap">
                    <label>Secondary color</label>
                    <input type="text"
                           autocomplete="no"
                           class="form-control"
                           placeholder="Enter #"
                           maxlength="7"
                           v-model="solidText.color"
                           @change="styleButtonText"
                    />
                  </div>
                </div>
              </div>

              <h2 class="l-header-2">Button style</h2>
              <br />
              <span class="section-disclaimer">
                You can choose a style for the button.
              </span>
              <div class="row mb-3 mt-2 pl-3">
                <div class="row mb-3 mt-4">
                  <div class="d-flex col-12">
                    <div class="d-flex col-6 pl-0">
                      <div class="solid-button button-full" :style="solidBackground">
                        <span class="text-uppercase" :style="solidText">
                          quick quote
                        </span>
                      </div>
                    </div>
                    <div class="d-flex flex-column col-6 pl-0">
                      <div class="check-field mb-3">
                        <label for="solid" :class="[buttonStyle.includes('1') ? 'check-open' : '']">
                          <span class="checkmark" />Solid button
                          <input type="checkbox"
                                 v-model="buttonStyle"
                                 class="btn-check"
                                 id="solid"
                                 :value="1"
                                 @change="uniqueCheckButtonStyle"
                          />
                        </label>
                      </div>
                      <span class="option-explanation">
                        With this option the button will be in one solid color taken from your primary color.
                      </span>
                    </div>
                  </div>
                </div>
                <div class="row mb-3 mt-4">
                  <div class="d-flex col-12">
                    <div class="d-flex col-6 pl-0">
                      <div class="outline-button button-full" :style="outlineBackground">
                        <span class="text-uppercase" :style="outlineText">
                          quick quote
                        </span>
                      </div>
                    </div>
                    <div class="d-flex col-6 flex-column pl-0">
                      <div class="check-field mb-3">
                        <label for="outline" :class="[buttonStyle.includes('2') ? 'check-open' : '']">
                          <span class="checkmark" />Outline button
                          <input type="checkbox"
                                 v-model="buttonStyle"
                                 class="btn-check"
                                 id="outline"
                                 :value="2"
                                 @change="uniqueCheckButtonStyle"
                          />
                        </label>
                      </div>
                      <span class="option-explanation">
                        With this option the button will have an outline and text in your primary color.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row l-m-b-16">
            <div class="col-6">
              <h2 class="l-header-2">Form style</h2>
              <br />
              <span class="section-disclaimer">
                Choose between side modal opening or a simple pop-up modal for your quick quote form.
                For both styles the form will remain the same on mobile devices.
              </span>
              <div class="row mb-3 mt-4">
                <div class="d-flex col-12">
                  <div class="d-flex col-6 pl-0">
                    <img src="../../assets/img/side-modal.gif" width="240" alt="Side modal example" />
                  </div>
                  <div class="d-flex flex-column col-6 pl-0">
                    <div class="check-field mb-3">
                      <label for="side" :class="[modalPosition.includes('1') ? 'check-open' : '']">
                        <span class="checkmark" />Side modal
                        <input type="checkbox"
                               v-model="modalPosition"
                               class="btn-check"
                               id="side"
                               :value="1"
                               @change="uniqueCheckModal"
                        />
                      </label>
                    </div>
                    <span class="option-explanation">
                      With this option the modal will open from the right side of the screen.
                    </span>
                  </div>
                </div>
              </div>
              <div class="row mb-3 mt-4">
                <div class="d-flex col-12">
                  <div class="d-flex col-6 pl-0">
                    <img src="../../assets/img/pop-up.gif" width="240" alt="Pop-up modal" />
                  </div>
                  <div class="d-flex col-6 flex-column pl-0">
                    <div class="check-field mb-3">
                      <label for="pop-up" :class="[modalPosition.includes('2') ? 'check-open' : '']">
                        <span class="checkmark" />Pop-up
                        <input type="checkbox"
                               v-model="modalPosition"
                               class="btn-check"
                               id="pop-up"
                               :value="2"
                               @change="uniqueCheckModal"
                        />
                      </label>
                    </div>
                    <span class="option-explanation">
                      With this option the modal will open as a pop-up in the middle of the screen.
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-6">
              <h2 class="l-header-2">Script for your website</h2>
              <br />
              <span class="section-disclaimer">
                Click on the text below to copy the script to clipboard. Add it to your HTML app by pasting it before &lt;/body&gt; tag.
                <a href="#" class="assistance" @click="toggleAssistanceModal">Need more assistance?</a>
              </span>
              <div class="row mb-3 mt-3 pl-3 pr-3">
                <div class="select-wrap h-auto">
                  <!-- eslint-disable-next-line -->
                  <textarea class="select-wrap-textarea form-control" rows="8" ref="script" @click="doCopy">&lt;script id="mod24-widget" src="{{ url === 'production' ? 'https://widget.mod24.com/widget.js' : 'https://widget.24mod.com/widget.js' }}" company-key="{{ company.web_key }}" button-position="{{ buttonPosition[0] }}" button-style="{{ buttonStyle[0] }}" modal-position="{{ modalPosition[0] }}" color-primary="{{ solidBackground.backgroundColor }}" color-secondary="{{ outlineBackground.backgroundColor }}" async&gt;&lt;/script&gt;
                </textarea>
                </div>
              </div>
              <div class="row mt-3 pl-3 pr-3 d-flex justify-content-end">
                <div class="assistance" @click="setFormType(2)">
                  Switch to custom form?
                </div>
              </div>
              <div class="row mt-3 pl-3 pr-3 d-flex justify-content-end">
                <div class="assistance" @click="setFormType(3)">
                  Switch to iframe form?
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
      <template v-if="form_type === 2">
        <CompaniesWebQuoteCustom
          :company="company"
          @setStepEditing="setFormType(1)"
          @setStepIframe="setFormType(3)"
        />
      </template>
      <template v-if="form_type === 3">
        <CompaniesWebQuoteIframe
          :company="company"
          @setStepEditing="setFormType(1)"
          @setStepCustom="setFormType(2)"
          @setStepIframe="setFormType(3)"
        />
      </template>
    </template>

    <modal v-if="assistanceModal" @close="toggleAssistanceModal" size="l-modal-large">
      <div slot="header">
        <h1>Web Quote Assistant</h1>
      </div>
      <div slot="body" style="">
        <template>
          <div class="container-fluid">
            <div class="row">
              <div class="col-12 pl-0">
                <div class="assistance-content mt-2 mb-3">
                  <span>
                    Simply copy the script from the web quote page and insert it at the bottom of your content, just before the &lt;/body&gt; tag of your HTML Application:
                  </span>
                  <code>
                    <div>
                      &lt;head&gt;...&lt;/head&gt;
                    </div>
                    <div>
                      &lt;body&gt;
                    </div>
                    <div>
                      &lt;h1&lt;Hello World!&lt;/h1&gt;
                    </div>
                    <div>
                      <b>Insert script here</b>
                    </div>
                    <div>
                      &lt;/body&gt;
                    </div>
                    <div>
                      &lt;/html&gt;
                    </div>
                  </code>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div slot="footer">
        <span />
      </div>
    </modal>
  </div>
</template>
<script>
import axios from 'axios';
import toastr from 'toastr';
import { mapMutations, mapState } from 'vuex';
import Errors from '../../classes/Errors';
import CompaniesVisibilityBar from './CompaniesVisibilityBar';
import CompaniesWebQuoteSelect from './CompaniesWebQuoteSelect';
import CompaniesWebQuoteCustom from './CompaniesWebQuoteCustom';
import CompaniesWebQuoteIframe from './CompaniesWebQuoteIframe';
import Modal from '../Modal';

export default {
  components: {
    CompaniesVisibilityBar,
    CompaniesWebQuoteSelect,
    CompaniesWebQuoteCustom,
    CompaniesWebQuoteIframe,
    Modal,
  },
  props: {
    type: {
      type: String,
      default: 'edit',
    },
  },
  data: () => ({
    buttonPosition: [],
    modalPosition: [],
    buttonStyle: [],
    solidBackground: {
      backgroundColor: '',
      borderColor: '',
    },
    solidText: {
      color: '',
    },
    outlineBackground: {
      backgroundColor: '',
      borderColor: '',
    },
    outlineText: {
      color: '',
    },
    url: process.env.NODE_ENV,
    form_type: 0,
    assistanceModal: false,
    loadingButton: false,
    buttonDisabled: false,
    showTooltipPrefix: false,
    loading: false,
    editing: false,
    errors: new Errors(),
  }),
  computed: {
    ...mapState({
      company: state => state.companies.company,
    }),
  },
  mounted() {
    this.getCompanyForm();
  },
  methods: {
    ...mapMutations(['setCompanyStep', 'setCompany']),
    getCompanyForm() {
      this.loading = true;
      axios
        .get(`/companies/${this.company.id}/forms`)
        .then(res => {
          this.buttonPosition[0] = JSON.stringify(res.data.data.button_position);
          this.buttonStyle[0] = JSON.stringify(res.data.data.button_style);
          this.modalPosition[0] = JSON.stringify(res.data.data.form_style);
          this.solidBackground.backgroundColor = res.data.data.primary_color;
          this.outlineBackground.borderColor = res.data.data.primary_color;
          this.outlineBackground.backgroundColor = res.data.data.secondary_color;
          this.solidText.color = res.data.data.secondary_color;
          this.outlineText.color = res.data.data.primary_color;
          if (res.data.data.form_type) {
            this.form_type = res.data.data.form_type;
          }
          if (this.solidBackground.backgroundColor) {
            this.editing = true;
          }
        })
        .finally(() => {
          this.loading = false;
        })
        .catch(err => {
          console.error(err);
        });
    },
    save() {
      this.loadingButton = true;
      this.buttonDisabled = true;
      if (!this.editing) {
        this.createForm();
      } else {
        this.editForm();
      }
    },
    loadCompany() {
      axios
        .get(`companies/${this.company.id}`)
        .then(res => {
          res.data.data.zip = res.data.data.zip.zip;
          this.setCompany(res.data.data);
          this.getCompanyPaymentGateway();
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    createForm() {
      axios
        .post(`/companies/${this.company.id}/forms`, {
          button_position: this.buttonPosition[0],
          button_style: this.buttonStyle[0],
          form_style: this.modalPosition[0],
          primary_color:
            this.buttonStyle[0] === 1
              ? this.solidBackground.backgroundColor
              : this.outlineBackground.borderColor,
          secondary_color:
            this.buttonStyle[0] === 1
              ? this.solidText.color
              : this.outlineBackground.backgroundColor,
          form_type: this.form_type,
        })
        .then(() => {
          this.editing = true;
          toastr.success('Successfully created form');
          this.$emit('saved');
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    editForm() {
      axios
        .put(`/companies/${this.company.id}/forms`, {
          button_position: this.buttonPosition[0],
          button_style: this.buttonStyle[0],
          form_style: this.modalPosition[0],
          primary_color:
            this.buttonStyle[0] === 1
              ? this.solidBackground.backgroundColor
              : this.outlineBackground.borderColor,
          secondary_color:
            this.buttonStyle[0] === 1
              ? this.solidText.color
              : this.outlineBackground.backgroundColor,
          form_type: this.form_type,
        })
        .then(() => {
          toastr.success('Successfully edited form');
          this.$emit('saved');
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    doCopy() {
      this.$copyText(this.$refs.script.defaultValue).then(() => {
        toastr.success('Copied script to clipboard');
      });
    },
    styleButton(e) {
      this.solidBackground.backgroundColor = this.addHash(e.target.value);
      this.solidBackground.borderColor = this.addHash(e.target.value);
      this.outlineBackground.borderColor = this.addHash(e.target.value);
      this.outlineText.color = this.addHash(e.target.value);
    },
    styleButtonText(e) {
      this.solidText.color = this.addHash(e.target.value);
      this.outlineBackground.backgroundColor = this.addHash(e.target.value);
    },
    uniqueCheck(e) {
      this.buttonPosition = [];
      if (e.target.checked) {
        this.buttonPosition.push(e.target.value);
      }
    },
    uniqueCheckModal(e) {
      this.modalPosition = [];
      if (e.target.checked) {
        this.modalPosition.push(e.target.value);
      }
    },
    uniqueCheckButtonStyle(e) {
      this.buttonStyle = [];
      if (e.target.checked) {
        this.buttonStyle.push(e.target.value);
      }
    },
    addHash(value) {
      const hash = '#';
      let concatedValue = '';
      if (value.length <= 6 && !value.includes('#')) {
        concatedValue = hash.concat(value);
      } else {
        concatedValue = value;
      }

      return concatedValue;
    },
    toggleAssistanceModal() {
      this.assistanceModal = !this.assistanceModal;
    },
    setFormType(type) {
      this.form_type = type;
    },
  },
};
</script>
<style lang="scss" scoped>
.select-wrap {
  width: 100%;
}
.select-wrap-textarea {
  font-size: 11px;
  line-height: 13px;
  color: #616161;
  padding-top: 7px;
  resize: none;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/companies/CompaniesWebQuote.vue